"use client";

import { useEffect, useMemo, useState } from "react";
import { subDays } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import CzechRepublicMap, {
  type BranchMapMarker,
  type RegionId,
  getScoreTintColor,
} from "@/components/ui/map";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useBranch, type Branch } from "@/context/branch-context";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { buildScorePipeline, calculateScore } from "@/lib/branch-score";
import type { TranslationKey } from "@/i18n/messages";

type BranchScoreAggregate = {
  _id?: unknown;
  totalImages?: unknown;
  totalPenalty?: unknown;
};

type StaticBranchLocation = {
  id: string;
  name: string;
  aliases: string[];
  regionKey: TranslationKey;
  regionId?: RegionId;
  latitude: number;
  longitude: number;
};

type ResolvedBranchLocation = {
  latitude: number;
  longitude: number;
  regionKey?: TranslationKey;
  regionId?: RegionId;
};

const STATIC_BRANCH_LOCATIONS: StaticBranchLocation[] = [
  {
    id: "brno-sever",
    name: "Brno-sever",
    aliases: ["Brno sever", "Brno Sever"],
    regionKey: "region.southMoravia",
    regionId: "south-moravia",
    latitude: 49.225,
    longitude: 16.61,
  },
  {
    id: "ostrava-jih",
    name: "Ostrava-jih",
    aliases: ["Ostrava jih", "Ostrava Jih"],
    regionKey: "region.moraviaSilesia",
    regionId: "moravia-silesia",
    latitude: 49.783,
    longitude: 18.255,
  },
  {
    id: "karvina",
    name: "Karviná",
    aliases: ["Karvina"],
    regionKey: "region.moraviaSilesia",
    regionId: "moravia-silesia",
    latitude: 49.845,
    longitude: 18.3,
  },
];

const SCORE_LEGEND: Array<{
  label?: string;
  labelKey?: TranslationKey;
  color: string;
}> = [
  { label: "95-100", color: "#16a34a" },
  { label: "85-94", color: "#f59e0b" },
  { label: "< 85", color: "#dc2626" },
  { labelKey: "common.noData", color: "#94a3b8" },
];

const readNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const normalizeBranchName = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const staticLocationsByAlias = new Map<string, StaticBranchLocation>();

STATIC_BRANCH_LOCATIONS.forEach((location) => {
  [location.name, ...location.aliases].forEach((alias) => {
    staticLocationsByAlias.set(normalizeBranchName(alias), location);
  });
});

const getStaticLocation = (branchName: string) =>
  staticLocationsByAlias.get(normalizeBranchName(branchName));

const resolveBranchLocation = (
  branch: Branch,
): ResolvedBranchLocation | undefined => {
  const staticLocation = getStaticLocation(branch.name);

  if (branch.latitude !== undefined && branch.longitude !== undefined) {
    return {
      latitude: branch.latitude,
      longitude: branch.longitude,
      regionKey: staticLocation?.regionKey,
      regionId: staticLocation?.regionId,
    };
  }

  return staticLocation;
};

export function BranchScoreMap({ className }: { className?: string }) {
  const {
    branches,
    selectedBranches,
    canSelectBranches,
    selectBranches,
  } = useBranch();
  const { processedDateRange } = useDashboardDateRange();
  const branchFilter = useBranchFilter();
  const { mutateAsync, isPending } = usePostAggregatePizzas({});
  const { t, tp, formatNumber } = useLocalization();
  const [aggregateRows, setAggregateRows] = useState<BranchScoreAggregate[]>(
    [],
  );

  const { start, end } = useMemo(() => {
    if (processedDateRange.start && processedDateRange.end) {
      return { start: processedDateRange.start, end: processedDateRange.end };
    }

    const endDate = new Date();
    return { start: subDays(endDate, 7), end: endDate };
  }, [processedDateRange]);

  useEffect(() => {
    let isMounted = true;

    async function fetchBranchScores() {
      const pipeline = buildScorePipeline(start, end, branchFilter, "$branch_id");

      try {
        const response = await mutateAsync({ data: { pipeline } });

        if (isMounted) {
          setAggregateRows(response.results as BranchScoreAggregate[]);
        }
      } catch (error) {
        console.error("Failed to fetch branch score map data", error);
        if (isMounted) {
          setAggregateRows([]);
        }
      }
    }

    fetchBranchScores();

    return () => {
      isMounted = false;
    };
  }, [branchFilter, end, mutateAsync, start]);

  const displayBranches = useMemo<Branch[]>(() => {
    if (selectedBranches.length > 0) {
      return selectedBranches;
    }

    const branchList = branches ?? [];
    const activeBranches = branchList.filter(
      (branch) => branch.enabled !== false,
    );

    return activeBranches.length > 0 ? activeBranches : branchList;
  }, [branches, selectedBranches]);

  const markers = useMemo<BranchMapMarker[]>(() => {
    const aggregatesByBranch = new Map(
      aggregateRows
        .filter((row) => typeof row._id === "string")
        .map((row) => [row._id as string, row]),
    );

    const branchMarkers: BranchMapMarker[] = [];

    displayBranches.forEach((branch) => {
      const location = resolveBranchLocation(branch);

      if (!location) {
        return;
      }

      const aggregate = branch.bb_branch_id
        ? aggregatesByBranch.get(branch.bb_branch_id)
        : undefined;
      const totalImages = readNumber(aggregate?.totalImages);
      const totalPenalty = readNumber(aggregate?.totalPenalty);

      branchMarkers.push({
        id:
          branch.id ||
          branch.bb_branch_id ||
          `branch-${normalizeBranchName(branch.name)}`,
        name: branch.name,
        region: location.regionKey ? t(location.regionKey) : undefined,
        regionId: location.regionId,
        latitude: location.latitude,
        longitude: location.longitude,
        score: calculateScore(totalImages, totalPenalty),
        totalImages,
      });
    });

    if (selectedBranches.length > 0) {
      return branchMarkers;
    }

    const representedStaticLocations = new Set(
      displayBranches
        .map((branch) => getStaticLocation(branch.name)?.id)
        .filter((id): id is string => Boolean(id)),
    );

    const staticMarkers = STATIC_BRANCH_LOCATIONS.filter(
      (location) => !representedStaticLocations.has(location.id),
    ).map((location) => ({
      id: `static-${location.id}`,
      name: location.name,
      region: t(location.regionKey),
      regionId: location.regionId,
      latitude: location.latitude,
      longitude: location.longitude,
      score: null,
      totalImages: 0,
    }));

    return [...branchMarkers, ...staticMarkers];
  }, [aggregateRows, displayBranches, selectedBranches.length, t]);

  const regionTints = useMemo<Partial<Record<string, string>>>(() => {
    const sums = new Map<string, { total: number; weight: number }>();

    markers.forEach((marker) => {
      if (!marker.regionId || marker.score === null) {
        return;
      }

      const current = sums.get(marker.regionId) ?? { total: 0, weight: 0 };
      current.total += marker.score;
      current.weight += 1;
      sums.set(marker.regionId, current);
    });

    const tints: Partial<Record<string, string>> = {};
    sums.forEach((value, key) => {
      if (value.weight > 0) {
        const average = value.total / value.weight;
        tints[key] = getScoreTintColor(average);
      }
    });

    return tints;
  }, [markers]);

  const missingPositionCount = useMemo(
    () =>
      displayBranches.filter((branch) => !resolveBranchLocation(branch)).length,
    [displayBranches],
  );

  const selectableBranchIds = useMemo(
    () => new Set((branches ?? []).map((branch) => branch.id)),
    [branches],
  );

  return (
    <Card
      className={cn(
        "overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-emerald-50/40 shadow-sm dark:to-emerald-950/20",
        className,
      )}
    >
      <CardHeader className="pb-3">
        <CardTitle>{t("map.title")}</CardTitle>
        <CardDescription>{t("map.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div
          className="relative aspect-[1000/570] w-full overflow-hidden rounded-md border border-primary/10 shadow-inner"
          style={{
            background:
              "radial-gradient(circle at 70% 22%, rgba(34, 197, 94, 0.22), transparent 30%), linear-gradient(135deg, rgba(14, 165, 233, 0.16), rgba(255, 255, 255, 0) 48%, rgba(16, 185, 129, 0.14))",
          }}
        >
          <CzechRepublicMap
            className="relative z-10"
            markers={markers}
            regionTints={regionTints}
            onMarkerClick={(marker) => {
              if (canSelectBranches && selectableBranchIds.has(marker.id)) {
                selectBranches([marker.id]);
              }
            }}
          />
          <div className="pointer-events-none absolute right-3 top-3 z-20 rounded-md border bg-background/90 p-2 shadow-sm backdrop-blur">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
              {SCORE_LEGEND.map((item) => {
                const label = item.label ?? t(item.labelKey as TranslationKey);

                return (
                  <div key={label} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {isPending
              ? t("map.loadingScores")
              : tp("map.branchCount", markers.length, {
                  count: formatNumber(markers.length),
                })}
          </span>
          {missingPositionCount > 0 && (
            <span>
              {tp("map.missingCoordinates", missingPositionCount, {
                count: formatNumber(missingPositionCount),
              })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
